import React, { useEffect, useMemo } from 'react';

import { ContextMenu } from '@base-ui/react/context-menu';

import type { DeclarativeMenuConfig } from './build-declarative-menu';
import { buildDeclarativeMenu } from './build-declarative-menu';

export interface DeclarativeContextMenuProps {
  config: DeclarativeMenuConfig;
  onClose: () => void;
  /** The viewport point the menu opens at, usually where the user right-clicked. */
  position: { left: number; top: number };
  zIndex?: number;
}

/**
 * A DeclarativeMenu that opens at a point instead of from a trigger. It is always open, so mount it
 * only while the menu should show.
 *
 * This uses Base UI's ContextMenu.Root without its trigger. A plain Menu.Root only joins Base UI's
 * menu tree through its trigger, so without one, opening a submenu closes the whole menu.
 */
export function DeclarativeContextMenu(props: DeclarativeContextMenuProps) {
  const { config, onClose, position, zIndex } = props;
  const { left, top } = position;

  const popup = useMemo(() => {
    const anchor = { getBoundingClientRect: () => DOMRect.fromRect({ x: left, y: top }) };
    return buildDeclarativeMenu(config, { anchor, zIndex });
  }, [config, left, top, zIndex]);

  /**
   * A right-click outside the menu lands on the menu's backdrop, which closes the menu. Without a
   * ContextMenu.Trigger to handle it, the browser would also show its own context menu.
   */
  useEffect(() => {
    document.addEventListener('contextmenu', preventDefault);
    return () => document.removeEventListener('contextmenu', preventDefault);
  }, []);

  return (
    <ContextMenu.Root
      open
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      {popup}
    </ContextMenu.Root>
  );
}

function preventDefault(event: Event): void {
  event.preventDefault();
}
